/**
 * orcamento-config-store — O1 (Ferramenta de Orçamento).
 *
 * Hooks TanStack Query pra a configuração da ferramenta (markups de material,
 * markup da mão de obra + condições de pagamento, taxas dos simuladores
 * SICOOB/GRENKE). Tudo servido por `GET /api/v1/orcamento-config` em 1 request
 * e mutado por endpoints granulares (admin). Gated no backend por
 * ORCAMENTO_ENABLED (a tela só renderiza com a flag ON).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export interface MarkupMaterial {
  id: string;
  percentual: number;
  isDefault: boolean;
  ordem: number;
  ativo: boolean;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

/** R1 (RESUMO-REGIME): regime de faturamento da cascata. */
export type RegimeFaturamento = 'direto' | 'revenda';

export interface OrcamentoConfigKV {
  markupMoPct: number;
  /** M1 (AJUSTES3): markup padrão PRÓPRIO do kit insumo (mig 373). Null só se
   * nada configurável existir — a UI orienta configurar, nunca assume número. */
  kitMarkupPadraoPct: number | null;
  condEntradaPct: number;
  condParceladoN: number;
  /** @deprecated R2 (RESUMO-REGIME): régua antiga de markup (Baixo/Médio/
   * Saudável) — o modelo morreu; a régua vigente é `cascataMargemSaudavelMin`.
   * Campos ficam só por compat de payload (o backend ainda os envia). */
  margemBaixoMax: number;
  /** @deprecated R2: ver `margemBaixoMax`. */
  margemMedioMax: number;
  /** CA-4: % de comissão provisório da cascata (construtor futuro substitui). */
  cascataComissaoPct: number;
  /** CA-4: régua da margem LÍQUIDA — ≥ este valor = saudável. */
  cascataMargemSaudavelMin: number;
  /** R1: regime de faturamento PADRÃO de todo orçamento (seed: direto). */
  cascataRegimePadrao: RegimeFaturamento;
  /** R1: liga o seletor de regime POR ORÇAMENTO no builder (nasce desligado). */
  cascataPermitirRegimePorOrcamento: boolean;
}

/** O4: regra de parcelamento por faixa de valor (NULL = sem teto). */
export interface ParcelamentoFaixa {
  id: string;
  ateCentavos: number | null;
  entradaPct: number;
  parcelas: number;
  ordem: number;
  ativo: boolean;
}

/** O7: kit de insumos reutilizável do catálogo. */
export interface KitCatalogoItem {
  id: string;
  descricao: string;
  valorCentavos: number;
  markupPct: number | null;
  ordem: number;
  ativo: boolean;
}

export type SimuladorTipo = 'sicoob' | 'grenke';

export interface SimuladorTaxa {
  id: string;
  tipo: SimuladorTipo;
  prazo: number;
  valor: number;
  ativo: boolean;
  ordem: number;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface OrcamentoConfigSnapshot {
  markups: MarkupMaterial[];
  config: OrcamentoConfigKV;
  taxas: SimuladorTaxa[];
  faixasParcelamento: ParcelamentoFaixa[];
  kitCatalogo: KitCatalogoItem[];
}

const KEY = ['orcamento-config'] as const;

/** GET /api/v1/orcamento-config — snapshot completo da config. */
export function useOrcamentoConfig() {
  return useQuery({
    queryKey: [...KEY, 'snapshot'] as const,
    queryFn: () => api.get<OrcamentoConfigSnapshot>('/api/v1/orcamento-config'),
    enabled: hasToken(),
  });
}

/** Mutations admin — markups / config KV / taxas. Invalida o snapshot. */
export function useOrcamentoConfigActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: KEY });

  const createMarkup = useMutation({
    mutationFn: (input: { percentual: number; ativo?: boolean }) =>
      api.post<MarkupMaterial>('/api/v1/orcamento-config/markups', input),
    onSuccess: invalidate,
  });
  const updateMarkup = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { percentual?: number; ativo?: boolean } }) =>
      api.patch<MarkupMaterial>(`/api/v1/orcamento-config/markups/${id}`, patch),
    onSuccess: invalidate,
  });
  const deleteMarkup = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/orcamento-config/markups/${id}`),
    onSuccess: invalidate,
  });
  const setDefaultMarkup = useMutation({
    mutationFn: (id: string) =>
      api.put<MarkupMaterial>(`/api/v1/orcamento-config/markups/${id}/default`, {}),
    onSuccess: invalidate,
  });

  const updateConfig = useMutation({
    mutationFn: (patch: Partial<OrcamentoConfigKV>) =>
      api.put<OrcamentoConfigSnapshot>('/api/v1/orcamento-config/config', patch),
    onSuccess: invalidate,
  });

  const createTaxa = useMutation({
    mutationFn: (input: { tipo: SimuladorTipo; prazo: number; valor: number; ativo?: boolean }) =>
      api.post<SimuladorTaxa>('/api/v1/orcamento-config/taxas', input),
    onSuccess: invalidate,
  });
  const updateTaxa = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { prazo?: number; valor?: number; ativo?: boolean };
    }) => api.patch<SimuladorTaxa>(`/api/v1/orcamento-config/taxas/${id}`, patch),
    onSuccess: invalidate,
  });
  const deleteTaxa = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/v1/orcamento-config/taxas/${id}`),
    onSuccess: invalidate,
  });

  // O4: faixas de parcelamento por valor.
  const createFaixa = useMutation({
    mutationFn: (input: { ateCentavos: number | null; entradaPct: number; parcelas: number; ordem?: number }) =>
      api.post<OrcamentoConfigSnapshot>('/api/v1/orcamento-config/faixas', input),
    onSuccess: invalidate,
  });
  const updateFaixa = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{ ateCentavos: number | null; entradaPct: number; parcelas: number; ordem: number; ativo: boolean }>;
    }) => api.patch<OrcamentoConfigSnapshot>(`/api/v1/orcamento-config/faixas/${id}`, patch),
    onSuccess: invalidate,
  });
  const deleteFaixa = useMutation({
    mutationFn: (id: string) => api.delete<OrcamentoConfigSnapshot>(`/api/v1/orcamento-config/faixas/${id}`),
    onSuccess: invalidate,
  });

  // O7: catálogo de kits de insumos.
  const createKit = useMutation({
    mutationFn: (input: { descricao: string; valorCentavos: number; markupPct?: number | null }) =>
      api.post<OrcamentoConfigSnapshot>('/api/v1/orcamento-config/kit-catalogo', input),
    onSuccess: invalidate,
  });
  const updateKit = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{ descricao: string; valorCentavos: number; markupPct: number | null; ativo: boolean }>;
    }) => api.patch<OrcamentoConfigSnapshot>(`/api/v1/orcamento-config/kit-catalogo/${id}`, patch),
    onSuccess: invalidate,
  });
  const deleteKit = useMutation({
    mutationFn: (id: string) => api.delete<OrcamentoConfigSnapshot>(`/api/v1/orcamento-config/kit-catalogo/${id}`),
    onSuccess: invalidate,
  });

  return {
    createMarkup,
    updateMarkup,
    deleteMarkup,
    setDefaultMarkup,
    updateConfig,
    createTaxa,
    updateTaxa,
    deleteTaxa,
    createFaixa,
    updateFaixa,
    deleteFaixa,
    createKit,
    updateKit,
    deleteKit,
  };
}
